import config from '../../tokens/layer.json';
import { overlayTemplate, layerTemplate } from '../templates/_layer.tpl.js';
import { template } from '../commons/_template.js';

/**
 * How to create a layer instance
 *
 * If layer exist on DOM
 * this.layer = new Layer({
        "classContent": '.js-myLayer', // string -> query selector
        "isScrollEdge": false|true, // optional, default false
        "onOpen": function() {
            console.log('open');
        },
        "onClose": function() {
            console.log('close');
        }
    });

  * If layer doesn't exist on DOM
    this.generatingLayer = new Layer({
        {
          "content": {
            "classContent": "",
            "title": { //required
                "text": "string", required
                "heading": "number", default 2
            },
            contentHTML: "",
            "footer": {
              "fstCta": { //required
                "label": "",
                "innerData": {
                  "data-toto": "",
                  "class": ""
                }
              },
              "sndCta": {
                "type": "link|button",
                "href": "#", // if type == link
                "label": "",
                "innerData": {
                  "data-toto": "",
                  "class": ""
                }
              },
            }
          }, // object -> layer generation
      "extended": false|true, // optional, default false
      "isScrollEdge": false|true, // optional, default false
      "onOpen": () => {},
      "onClose": () => {}
        }

    });
*/

const LAYER_CONSTANTS = {
  fstCtaSelector: 'js-layer-first-cta',
  sndCtaSelector: 'js-layer-second-cta',
  bodyClass: `${config.class}${config.open}`,
  dialogClass: `${config.class}${config.dialog}`,
  layerBodyClass: `${config.class}${config.body}`,
  layerContentClass: `${config.class}${config.content}`,
  overflowClass: `${config.class}${config.body}${config.overflowing}`,
  overflowEdgeClass: `${config.class}${config.body}${config.overflowing}${config.scrollEdge}`,
  layerOpenedClass: 'is-open',
  overlayOpenedClass: 'is-visible',
  hiddenClass: 'kl-hidden'
};
function $initEvents() {
  this.layer.delegateEvent('click', '.js-layer-close', e => {
    e.stopPropagation();
    this.close();
  });
}
const allLayers = [];
export default class Layer {
  /**
   * @constructor
   * @param {String} content : The layer's id attribute
   * @param {Object} options
   */
  constructor(prmOptions) {
    // init options
    const defaultOptions = {
      'classContent': null,
      'id': `layer-${allLayers.length + 1}`,
      'onOpen': () => {
        return true;
      },
      'onClose': () => {
        return true;
      }
    };
    this.options = Object.assign(defaultOptions, prmOptions);
    this.displayed = false;
    this.$oldFocusedElement = null;

    // get the layer or create it    //
    if (this.options.content !== undefined) {
      if (typeof this.options.content === 'object') {
        this.layer = this.generateLayerDOM();
      }
    } else {
      this.layer = document.querySelector(`.${this.options.classContent}`);
    }
    if (this.layer) {
      this.layerTitleLabel = document.getElementById(this.layer.getAttribute('aria-labelledby')).innerHTML;
      this.layerDialog = this.layer.querySelector(`.${LAYER_CONSTANTS.dialogClass}`);
      this.$fstCta = this.layer.querySelector(`.${LAYER_CONSTANTS.fstCtaSelector}`);
      this.$sndCta = this.layer.querySelector(`.${LAYER_CONSTANTS.sndCtaSelector}`);
      $initEvents.call(this);
    }

    // Add click event to close the layer
    this.layer.delegateEvent('click', '.js-close-layer', event => {
      event.stopPropagation();
      this.close();
    });

    // Add the overlay
    this.overlay = this.generateOverlayDOM();
    this.overlay.addEventListener('click', event => {
      if (this.layerDialog.matches(`.${LAYER_CONSTANTS.layerOpenedClass}`) && this.overlay.matches(`.${LAYER_CONSTANTS.overlayOpenedClass}`)) {
        event.stopPropagation();
        this.close();
      }
    });

    // Keyboard management
    this.escapeLayer();

    // Store all layers in this object
    allLayers.push(this);
  }
  open() {
    // Only one layer at a time
    this.closeOtherLayers();
    if (typeof this.options.onOpen === 'function') {
      this.options.onOpen(this);
    }
    document.body.classList.add(LAYER_CONSTANTS.bodyClass);
    this.displayed = true;

    // Give accessibility to the layer
    this.layer.setAttribute('tabindex', '0');
    this.layer.setAttribute('aria-hidden', 'false');

    // Get the first element of the layer to be able to loop
    this.firstFocusable = this.layer.querySelector('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || this.layer;
    this.trapFocus();

    // Open layer
    this.layerDialog.classList.add(LAYER_CONSTANTS.layerOpenedClass);
    this.overlay.classList.remove(LAYER_CONSTANTS.hiddenClass);
    this.overlay.classList.add(LAYER_CONSTANTS.overlayOpenedClass);

    // Get last focused element
    this.$oldFocusedElement = document.activeElement;

    // Focus first focusable element when layer is open
    const onTransitionEnd = () => {
      this.firstFocusable.focus({
        preventScroll: true
      });
    };
    this.layer.addEventListener('transitionend', onTransitionEnd, {
      once: true
    });

    // check if dialog is scrollable
    const contentHeight = this.layer.querySelector(`.${LAYER_CONSTANTS.layerContentClass}`).scrollHeight;
    const bodyHeight = this.layer.querySelector(`.${LAYER_CONSTANTS.layerBodyClass}`).offsetHeight;
    if (contentHeight > bodyHeight) {
      let overflowingClass = LAYER_CONSTANTS.overflowClass;
      if (this.options.isScrollEdge) {
        overflowingClass = LAYER_CONSTANTS.overflowEdgeClass;
      }
      this.layer.querySelector(`.${LAYER_CONSTANTS.layerBodyClass}`).classList.add(`${overflowingClass}`);
    }

    // Fix scroll
    document.children[0].style.scrollBehavior = 'auto';
    document.children[0].classList.add('is-scroll-locked');
  }
  close(options = {
    'chainedLayer': false
  }) {
    if (!options.chainedLayer) {
      if (typeof this.options.onClose === 'function') {
        this.options.onClose(this);
      }
      document.body.classList.remove(LAYER_CONSTANTS.bodyClass);
      this.displayed = false;

      // Remove accessibility from the layer
      this.layer.setAttribute('tabindex', '-1');
      this.layer.setAttribute('aria-hidden', 'true');

      // Hide layer
      this.layerDialog.classList.remove(LAYER_CONSTANTS.layerOpenedClass);
      this.overlay.classList.add(LAYER_CONSTANTS.hiddenClass);
      this.overlay.classList.remove(LAYER_CONSTANTS.overlayOpenedClass);

      // Remove fix scroll
      document.children[0].classList.remove('is-scroll-locked');

      // Restore focus
      if (this.$oldFocusedElement !== undefined && this.$oldFocusedElement !== null) this.$oldFocusedElement.focus();
    }

    // Remove accessibility from the layer
    this.layer.setAttribute('tabindex', '-1');
    this.layer.setAttribute('aria-hidden', 'true');
    this.displayed = false;
    this.layerDialog.classList.remove(LAYER_CONSTANTS.layerOpenedClass);
  }
  closeOtherLayers() {
    for (let i = 0; i < allLayers.length; i++) {
      if (allLayers[i].displayed) {
        allLayers[i].close({
          'chainedLayer': true
        });
      }
    }
  }
  trapFocus() {
    const focusableElements = this.layer.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    document.addEventListener('keydown', function (e) {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) {
        return;
      }
      if (e.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        // if tab key is pressed
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });
  }
  escapeLayer() {
    document.addEventListener('keydown', event => {
      if ((event.keyCode === '27' || event.code === 'Escape') && this.displayed) {
        this.close();
      }
    });
  }
  generateLayerDOM() {
    document.body.insertAdjacentHTML('beforeend', template(layerTemplate, this.options));
    return document.querySelector(`div#js-${this.options.id}`);
  }
  generateOverlayDOM() {
    if (!document.getElementById('js-layer-overlay')) {
      document.body.insertAdjacentHTML('beforeend', template(overlayTemplate));
    }
    return document.getElementById('js-layer-overlay');
  }
}